import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAdminSearchCourses, useAdminSearchUsers } from '@/hooks/use-admin'
import { useAuth } from '@/lib/auth'

export default function AdminEnrollmentsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()

  // User search state
  const [userNameInput, setUserNameInput] = useState('')
  const [userSearchQuery, setUserSearchQuery] = useState<string | null>(null)

  // Course search state
  const [courseTitleInput, setCourseTitleInput] = useState('')
  const [courseSearchQuery, setCourseSearchQuery] = useState<string | null>(null)

  const { data: usersResult, isLoading: usersLoading } = useAdminSearchUsers(
    userSearchQuery ? { name: userSearchQuery } : null,
  )
  const { data: coursesResult, isLoading: coursesLoading } = useAdminSearchCourses(
    courseSearchQuery ? { title: courseSearchQuery } : null,
  )

  const users = (usersResult as any) ?? []
  const courses = (coursesResult as any) ?? []

  const handleUserSearch = () => {
    if (userNameInput.trim()) {
      setUserSearchQuery(userNameInput.trim())
    }
  }

  const handleCourseSearch = () => {
    if (courseTitleInput.trim()) {
      setCourseSearchQuery(courseTitleInput.trim())
    }
  }

  const tenantSlug = user?.tenantSlug ?? ''

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">受講状況管理</h1>

      <div className="grid gap-6 md:grid-cols-2">
        {/* User search card */}
        <Card>
          <CardHeader>
            <CardTitle>ユーザー名で検索</CardTitle>
            <CardDescription>
              ユーザー名を入力して、個別の受講状況ページへ移動します。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="ユーザー名を入力..."
                value={userNameInput}
                onChange={(e) => setUserNameInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUserSearch()}
              />
              <Button onClick={handleUserSearch}>検索</Button>
            </div>

            {usersLoading && <Skeleton className="h-32" />}

            {userSearchQuery &&
              !usersLoading &&
              (Array.isArray(users) && users.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>名前</TableHead>
                      <TableHead>メール</TableHead>
                      <TableHead>操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u: any) => (
                      <TableRow key={u.id}>
                        <TableCell>{u.name || '-'}</TableCell>
                        <TableCell>{u.email || '-'}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              navigate(`/${tenantSlug}/admin/enrollments/user/${u.id}`)
                            }
                          >
                            受講状況を見る
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">
                  該当するユーザーは見つかりませんでした。
                </p>
              ))}
          </CardContent>
        </Card>

        {/* Course search card */}
        <Card>
          <CardHeader>
            <CardTitle>講座名で検索</CardTitle>
            <CardDescription>
              講座名を入力して、講座別の受講状況ページへ移動します。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="講座名を入力..."
                value={courseTitleInput}
                onChange={(e) => setCourseTitleInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCourseSearch()}
              />
              <Button onClick={handleCourseSearch}>検索</Button>
            </div>

            {coursesLoading && <Skeleton className="h-32" />}

            {courseSearchQuery &&
              !coursesLoading &&
              (Array.isArray(courses) && courses.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>タイトル</TableHead>
                      <TableHead>カテゴリ</TableHead>
                      <TableHead>操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {courses.map((c: any) => (
                      <TableRow key={c.id}>
                        <TableCell>{c.title || '-'}</TableCell>
                        <TableCell>{c.category || '-'}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              navigate(`/${tenantSlug}/admin/enrollments/course/${c.id}`)
                            }
                          >
                            受講状況を見る
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">
                  該当する講座は見つかりませんでした。
                </p>
              ))}
          </CardContent>
        </Card>
      </div>

      {/* Ratings link */}
      <Card>
        <CardHeader>
          <CardTitle>レッスン評価一覧</CardTitle>
          <CardDescription>
            ユーザーがレッスンに付けた評価を確認できます。フィルタリングやCSVエクスポートも可能です。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => navigate(`/${tenantSlug}/admin/ratings`)}>評価一覧を見る</Button>
        </CardContent>
      </Card>
    </div>
  )
}
